import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  Award,
  UserPlus,
  Loader2,
  RefreshCw,
  ChevronRight,
  BarChart3,
  Video,
  TrendingUp,
  Bookmark,
  BookmarkCheck,
  Crown,
  GraduationCap,
  Users,
  Zap,
} from 'lucide-react';
import { mockSports } from '../mock';
import AdvancedStatsModal from '../components/AdvancedStatsModal';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common positions for basketball (can be expanded for other sports)
const POSITIONS = [
  { id: 'all', name: 'All Positions' },
  // Basketball
  { id: 'point guard', name: 'Point Guard', sport: 'Basketball' },
  { id: 'shooting guard', name: 'Shooting Guard', sport: 'Basketball' },
  { id: 'small forward', name: 'Small Forward', sport: 'Basketball' },
  { id: 'power forward', name: 'Power Forward', sport: 'Basketball' },
  { id: 'center', name: 'Center', sport: 'Basketball' },
  { id: 'guard', name: 'Guard', sport: 'Basketball' },
  { id: 'forward', name: 'Forward', sport: 'Basketball' },
  { id: 'wing', name: 'Wing', sport: 'Basketball' },
  // Soccer
  { id: 'goalkeeper', name: 'Goalkeeper', sport: 'Soccer' },
  { id: 'center back', name: 'Center Back', sport: 'Soccer' },
  { id: 'left back', name: 'Left Back', sport: 'Soccer' },
  { id: 'right back', name: 'Right Back', sport: 'Soccer' },
  { id: 'defensive midfielder', name: 'Defensive Midfielder', sport: 'Soccer' },
  { id: 'central midfielder', name: 'Central Midfielder', sport: 'Soccer' },
  { id: 'attacking midfielder', name: 'Attacking Midfielder', sport: 'Soccer' },
  { id: 'left wing', name: 'Left Wing', sport: 'Soccer' },
  { id: 'right wing', name: 'Right Wing', sport: 'Soccer' },
  { id: 'striker', name: 'Striker', sport: 'Soccer' },
  { id: 'midfielder', name: 'Midfielder', sport: 'Soccer' },
  { id: 'defender', name: 'Defender', sport: 'Soccer' },
  // Volleyball
  { id: 'setter', name: 'Setter', sport: 'Volleyball' },
  { id: 'libero', name: 'Libero', sport: 'Volleyball' },
  { id: 'outside hitter', name: 'Outside Hitter', sport: 'Volleyball' },
  { id: 'middle blocker', name: 'Middle Blocker', sport: 'Volleyball' },
  { id: 'opposite hitter', name: 'Opposite Hitter', sport: 'Volleyball' },
  { id: 'defensive specialist', name: 'Defensive Specialist', sport: 'Volleyball' },
  // Football
  { id: 'quarterback', name: 'Quarterback', sport: 'Football' },
  { id: 'running back', name: 'Running Back', sport: 'Football' },
  { id: 'wide receiver', name: 'Wide Receiver', sport: 'Football' },
  { id: 'tight end', name: 'Tight End', sport: 'Football' },
  { id: 'offensive lineman', name: 'Offensive Lineman', sport: 'Football' },
  { id: 'defensive end', name: 'Defensive End', sport: 'Football' },
  { id: 'defensive tackle', name: 'Defensive Tackle', sport: 'Football' },
  { id: 'linebacker', name: 'Linebacker', sport: 'Football' },
  { id: 'cornerback', name: 'Cornerback', sport: 'Football' },
  { id: 'safety', name: 'Safety', sport: 'Football' },
  { id: 'kicker', name: 'Kicker', sport: 'Football' },
  // Flag Football
  { id: 'flag quarterback', name: 'QB (Flag)', sport: 'Flag Football' },
  { id: 'flag receiver', name: 'Receiver (Flag)', sport: 'Flag Football' },
  { id: 'flag rusher', name: 'Rusher (Flag)', sport: 'Flag Football' },
  { id: 'flag center', name: 'Center (Flag)', sport: 'Flag Football' },
  // Lacrosse
  { id: 'attackman', name: 'Attackman', sport: 'Lacrosse' },
  { id: 'midfielder lax', name: 'Midfielder', sport: 'Lacrosse' },
  { id: 'defenseman', name: 'Defenseman', sport: 'Lacrosse' },
  { id: 'goalie', name: 'Goalie', sport: 'Lacrosse' },
  { id: 'faceoff specialist', name: 'Faceoff Specialist', sport: 'Lacrosse' },
  { id: 'long stick midfielder', name: 'Long Stick Midfielder', sport: 'Lacrosse' },
];

// Graduation years (current year + 4 years ahead)
const currentYear = new Date().getFullYear();
const GRAD_YEARS = [
  { id: 'all', name: 'All Years' },
  ...Array.from({ length: 6 }, (_, i) => ({
    id: String(currentYear + i),
    name: String(currentYear + i),
  })),
];

// Sport accent colors for profile borders
const getSportStyle = (sport) => {
  const s = (sport || '').toLowerCase();
  if (s.includes('basketball')) return { border: 'border-orange-500', ring: 'ring-orange-500/30', tag: 'bg-orange-500/15 text-orange-400 border-orange-500/30', color: 'orange' };
  if (s.includes('soccer') || s.includes('futbol')) return { border: 'border-green-500', ring: 'ring-green-500/30', tag: 'bg-green-500/15 text-green-400 border-green-500/30', color: 'green' };
  if (s.includes('volleyball')) return { border: 'border-blue-500', ring: 'ring-blue-500/30', tag: 'bg-blue-500/15 text-blue-400 border-blue-500/30', color: 'blue' };
  if (s.includes('football')) return { border: 'border-purple-500', ring: 'ring-purple-500/30', tag: 'bg-purple-500/15 text-purple-400 border-purple-500/30', color: 'purple' };
  if (s.includes('lacrosse')) return { border: 'border-yellow-500', ring: 'ring-yellow-500/30', tag: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30', color: 'yellow' };
  return { border: 'border-cyan-500', ring: 'ring-cyan-500/30', tag: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30', color: 'cyan' };
};

const Athletes = () => {
  const { token, isAuthenticated, hasRequiredTier } = useAuth();
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [selectedGradYear, setSelectedGradYear] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [bookmarkLoading, setBookmarkLoading] = useState(null);
  const [selectedAthleteForStats, setSelectedAthleteForStats] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);

  // Check if user can bookmark athletes (Elite tier required for recruiting features)
  const canBookmark = isAuthenticated() && hasRequiredTier('elite');

  const fetchAthletes = useCallback(async () => {
    try {
      let url = `${API_URL}/api/athletes`;
      const params = new URLSearchParams();
      if (selectedSport !== 'all') params.append('sport', selectedSport);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;

      const res = await fetch(url);
      if (res.ok) {
        let data = await res.json();
        // Filter out test athletes
        data = data.filter(a => {
          const name = (a.name || '').toLowerCase();
          return !name.startsWith('test_') && !name.startsWith('test ');
        });
        
        // Client-side position filter
        if (selectedPosition !== 'all') {
          data = data.filter(a => {
            const pos = (a.position || '').toLowerCase();
            const filterId = selectedPosition.toLowerCase();
            return pos.includes(filterId) || filterId.includes(pos);
          });
        }
        
        // Client-side graduation year filter
        if (selectedGradYear !== 'all') {
          data = data.filter(a => {
            const gradYear = a.graduation_year || a.grad_year;
            return gradYear && String(gradYear) === selectedGradYear;
          });
        }
        
        setAthletes(data);
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
    }
    setLoading(false);
    setRefreshing(false);
  }, [selectedSport, selectedPosition, selectedGradYear, searchQuery]);

  const fetchBookmarkedIds = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/user/bookmarked-athletes/ids`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const ids = await res.json();
        setBookmarkedIds(ids);
      }
    } catch (error) {
      console.error('Error fetching bookmarked athletes:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchAthletes();
  }, [fetchAthletes]);

  useEffect(() => {
    fetchBookmarkedIds();
  }, [fetchBookmarkedIds]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAthletes();
  };

  const handleBookmarkAthlete = async (e, athleteId, athleteName) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated()) {
      toast.error('Please sign in to bookmark athletes');
      return;
    }
    
    if (!canBookmark) {
      toast.error('Upgrade to Elite to build your recruiting watchlist');
      return;
    }

    setBookmarkLoading(athleteId);
    const isBookmarked = bookmarkedIds.includes(athleteId);
    
    try {
      const res = await fetch(`${API_URL}/api/user/bookmarked-athletes/${athleteId}`, {
        method: isBookmarked ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        if (isBookmarked) {
          setBookmarkedIds(prev => prev.filter(id => id !== athleteId));
          toast.success(`Removed ${athleteName} from watchlist`);
        } else {
          setBookmarkedIds(prev => [...prev, athleteId]);
          toast.success(`Added ${athleteName} to watchlist`);
        }
      } else {
        toast.error('Failed to update watchlist');
      }
    } catch (error) {
      toast.error('Error updating watchlist');
    } finally {
      setBookmarkLoading(null);
    }
  };

  const handleClaimProfile = async (athleteId, athleteName) => {
    if (!isAuthenticated()) {
      toast.error('Please login to claim a profile');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/athletes/${athleteId}/claim`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(`Successfully claimed profile for ${athleteName}!`);
        fetchAthletes();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to claim profile');
      }
    } catch (error) {
      toast.error('Error claiming profile');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#00d4ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="athletes-page">
      <SeoHead
        title="Athletes — Browse Profiles, Stats & Highlights"
        description="Discover athletes streaming on Rooam Sports Network. Browse profiles, view real-time stats, watch highlight clips, and follow players across AAU, amateur, and youth basketball."
        path="/athletes"
      />
      {/* Pull to refresh indicator */}
      {refreshing && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-[#00d4ff] text-black px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="font-medium text-sm">Refreshing...</span>
        </div>
      )}

      {/* Cinematic Hero Section */}
      <div className="relative h-[35vh] min-h-[280px] max-h-[400px] overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1583079806406-91731880e785?auto=format&fit=crop&w=2000&q=80')` 
          }}
        />
        {/* Dark Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/50 via-transparent to-[#0A0A0A]/50" />
        
        {/* Hero Content */}
        <div className="relative h-full max-w-7xl mx-auto px-4 flex flex-col justify-end pb-8 md:pb-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="font-manrope text-[#ff0000] font-bold text-[10px] sm:text-xs tracking-[0.2em] uppercase mb-2 flex items-center gap-2">
                <Users className="h-3.5 w-3.5" />
                Scout Mode
              </p>
              <h1 
                className="font-oswald text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                Athlete Directory
              </h1>
              <p className="text-sm md:text-base text-zinc-400 mt-3 max-w-xl">
                Find elite talent. Filter by sport, position, and graduation year. 
                Scouts and coaches — your next recruit is here.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              {isAuthenticated() && (
                <Button
                  size="sm"
                  className="bg-[#00d4ff] hover:bg-[#33ddff] text-black font-bold uppercase tracking-wide text-xs h-9 px-4"
                  asChild
                  data-testid="create-profile-btn"
                >
                  <Link to="/athlete/create">
                    <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                    Create Profile
                  </Link>
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10 h-9 px-3"
                onClick={handleRefresh}
                disabled={refreshing}
                data-testid="refresh-athletes-btn"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Command Bar - Sticky Filter Section */}
      <div className="sticky top-16 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Row 1: Search + Dropdowns */}
          <div className="flex flex-col sm:flex-row gap-3 mb-3">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="Search athletes, teams, positions..."
                className="pl-10 bg-white/5 border-white/10 text-white placeholder-zinc-500 text-sm h-10 focus:border-[#00d4ff]/50 focus:ring-1 focus:ring-[#00d4ff]/30"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="athlete-search-input"
              />
            </div>
            
            {/* Position Dropdown */}
            <Select value={selectedPosition} onValueChange={setSelectedPosition}>
              <SelectTrigger 
                className="w-full sm:w-[180px] bg-[#141414] border-white/10 text-white h-10"
                data-testid="position-select"
              >
                <SelectValue placeholder="Position" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10 max-h-[300px]">
                {POSITIONS
                  .filter(pos => pos.id === 'all' || selectedSport === 'all' || !pos.sport || pos.sport.toLowerCase() === selectedSport.toLowerCase())
                  .map((pos) => (
                  <SelectItem 
                    key={pos.id} 
                    value={pos.id}
                    className="text-white hover:bg-white/10 focus:bg-white/10"
                    data-testid={`position-option-${pos.id}`}
                  >
                    {pos.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Grad Year Dropdown */}
            <Select value={selectedGradYear} onValueChange={setSelectedGradYear}>
              <SelectTrigger 
                className="w-full sm:w-[140px] bg-[#141414] border-white/10 text-white h-10"
                data-testid="gradyear-select"
              >
                <GraduationCap className="h-4 w-4 mr-2 text-zinc-400" />
                <SelectValue placeholder="Grad Year" />
              </SelectTrigger>
              <SelectContent className="bg-[#141414] border-white/10">
                {GRAD_YEARS.map((year) => (
                  <SelectItem 
                    key={year.id} 
                    value={year.id}
                    className="text-white hover:bg-white/10 focus:bg-white/10"
                    data-testid={`gradyear-option-${year.id}`}
                  >
                    {year.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Row 2: Sport Pills */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <button
              onClick={() => { setSelectedSport('all'); setSelectedPosition('all'); }}
              data-testid="sport-filter-all"
              className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ${
                selectedSport === 'all'
                  ? 'bg-white text-black'
                  : 'border border-white/20 text-white hover:bg-white/10'
              }`}
            >
              All Sports
            </button>
            {mockSports.map((sport) => (
              <button
                key={sport.id}
                onClick={() => { setSelectedSport(sport.id); setSelectedPosition('all'); }}
                data-testid={`sport-filter-${sport.id}`}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ${
                  selectedSport === sport.id
                    ? 'bg-white text-black'
                    : 'border border-white/20 text-white hover:bg-white/10'
                }`}
              >
                {sport.name}
              </button>
            ))}
            
            {/* Results count */}
            <span className="ml-auto text-xs text-zinc-500">
              <span className="font-bold text-white">{athletes.length}</span> found
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Athletes Grid */}
        {athletes.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-zinc-600" />
            </div>
            <h3 
              className="text-2xl font-black text-white uppercase tracking-tight mb-2"
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              No Athletes Found
            </h3>
            <p className="text-zinc-500 mb-6 text-sm">
              {searchQuery ? 'Try a different search term' : 'Athletes will appear here once added'}
            </p>
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => { setSelectedSport('all'); setSelectedPosition('all'); setSelectedGradYear('all'); setSearchQuery(''); }}
              data-testid="clear-all-filters-btn"
            >
              Clear All Filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {athletes.map((athlete) => {
              const sportStyle = getSportStyle(athlete.sport);
              const hasStats = athlete.stats && Object.keys(athlete.stats).length > 0;
              const isBookmarked = bookmarkedIds.includes(athlete.id);

              return (
                <Link
                  key={athlete.id}
                  to={`/athlete/${athlete.id}`}
                  data-testid={`athlete-card-${athlete.id}`}
                  className="group block"
                >
                  <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden hover:border-[#00d4ff]/50 hover:shadow-[0_0_30px_rgba(0,212,255,0.1)] transition-all duration-300">
                    {/* Profile Header */}
                    <div className="p-5 pb-4">
                      <div className="flex items-start gap-4">
                        {/* Avatar with sport-colored ring */}
                        <div className={`relative shrink-0 rounded-full ring-2 ${sportStyle.ring}`}>
                          <img
                            src={athlete.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${athlete.id}`}
                            alt={athlete.name}
                            className={`w-16 h-16 rounded-full border-2 ${sportStyle.border} object-cover`}
                          />
                          {athlete.verified && (
                            <div className="absolute -bottom-0.5 -right-0.5 bg-[#00d4ff] rounded-full p-0.5">
                              <Award className="h-3 w-3 text-black" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <h3 
                            className="font-black text-white text-base uppercase tracking-tight truncate group-hover:text-[#00d4ff] transition-colors duration-200"
                            style={{ fontFamily: 'Oswald, sans-serif' }}
                          >
                            {athlete.name}
                          </h3>
                          <p className="text-sm text-zinc-400 font-medium">{athlete.position}</p>
                          <p className="text-xs text-zinc-600 truncate">{athlete.team}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={`text-[10px] font-bold uppercase tracking-wide border ${sportStyle.tag}`}>
                              {athlete.sport}
                            </Badge>
                            {athlete.number > 0 && (
                              <span className="text-xs text-zinc-500 font-mono">#{athlete.number}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stats Section */}
                    {hasStats && (
                      <div className="mx-5 mb-4 border-t border-white/10 pt-4">
                        <div className="grid grid-cols-4 gap-2">
                          {Object.entries(athlete.stats).slice(0, 4).map(([key, value]) => (
                            <div key={key} className="text-center">
                              <div 
                                className="text-lg font-black text-white tabular-nums"
                                style={{ fontFamily: 'Oswald, sans-serif' }}
                              >
                                {value}
                              </div>
                              <div 
                                className="text-[9px] text-zinc-500 uppercase tracking-[0.15em] font-bold"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                              >
                                {key}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quick Stats Bar */}
                    <div className="mx-5 mb-4 grid grid-cols-3 gap-2 border-t border-white/10 pt-4">
                      <div className="text-center">
                        <div 
                          className="text-base font-black text-white"
                          style={{ fontFamily: 'Oswald, sans-serif' }}
                        >
                          {athlete.games || 0}
                        </div>
                        <div 
                          className="text-[9px] text-zinc-600 uppercase tracking-[0.15em] font-bold"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          Games
                        </div>
                      </div>
                      <div className="text-center">
                        <div 
                          className="text-base font-black text-[#00d4ff]"
                          style={{ fontFamily: 'Oswald, sans-serif' }}
                        >
                          {athlete.highlights || 0}
                        </div>
                        <div 
                          className="text-[9px] text-zinc-600 uppercase tracking-[0.15em] font-bold"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          Highlights
                        </div>
                      </div>
                      <div className="text-center">
                        <div 
                          className="text-base font-black text-white"
                          style={{ fontFamily: 'Oswald, sans-serif' }}
                        >
                          {athlete.age || '-'}
                        </div>
                        <div 
                          className="text-[9px] text-zinc-600 uppercase tracking-[0.15em] font-bold"
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          Age
                        </div>
                      </div>
                    </div>

                    {/* Advanced Stats Button - Tactical Red Theme */}
                    <div className="mx-5 mb-4">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedAthleteForStats(athlete);
                          setShowStatsModal(true);
                        }}
                        data-testid={`advanced-stats-btn-${athlete.id}`}
                        className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-[#ff0000]/10 to-transparent border border-[#ff0000]/20 rounded-lg py-2.5 hover:from-[#ff0000]/20 hover:border-[#ff0000]/40 transition-all duration-200 group/stats"
                      >
                        <Zap className="h-4 w-4 text-[#ff0000]" />
                        <span 
                          className="text-xs font-bold text-[#ff0000] uppercase tracking-wide"
                          style={{ fontFamily: 'Oswald, sans-serif' }}
                        >
                          Advanced Stats
                        </span>
                        <ChevronRight className="h-3.5 w-3.5 text-[#ff0000]/60 ml-auto" />
                      </button>
                    </div>

                    {/* Actions */}
                    <div className="px-5 pb-5 flex gap-2">
                      {/* Bookmark Button - Elite Feature */}
                      <Button
                        variant="outline"
                        size="sm"
                        className={`shrink-0 h-9 w-9 p-0 rounded-lg ${
                          isBookmarked
                            ? 'border-amber-500/50 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                            : 'border-white/10 text-zinc-400 hover:bg-white/5 hover:text-white hover:border-white/20'
                        } ${!canBookmark && isAuthenticated() ? 'opacity-60' : ''}`}
                        onClick={(e) => handleBookmarkAthlete(e, athlete.id, athlete.name)}
                        disabled={bookmarkLoading === athlete.id}
                        data-testid={`bookmark-btn-${athlete.id}`}
                        title={canBookmark ? (isBookmarked ? 'Remove from watchlist' : 'Add to watchlist') : 'Elite feature'}
                      >
                        {bookmarkLoading === athlete.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isBookmarked ? (
                          <BookmarkCheck className="h-4 w-4" />
                        ) : !canBookmark && isAuthenticated() ? (
                          <Crown className="h-4 w-4 text-amber-500" />
                        ) : (
                          <Bookmark className="h-4 w-4" />
                        )}
                      </Button>
                      
                      {athlete.claimable && !athlete.claimed_by && (
                        <Button
                          size="sm"
                          className="flex-1 bg-[#00d4ff] hover:bg-[#33ddff] text-black font-bold uppercase tracking-wide text-[10px] h-9 rounded-lg"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleClaimProfile(athlete.id, athlete.name);
                          }}
                          data-testid={`claim-btn-${athlete.id}`}
                        >
                          <UserPlus className="h-3.5 w-3.5 mr-1" />
                          Claim
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1 text-zinc-400 hover:text-white hover:bg-white/5 text-[10px] uppercase tracking-wide font-semibold h-9 rounded-lg"
                        data-testid={`view-profile-btn-${athlete.id}`}
                      >
                        View
                        <ChevronRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Advanced Stats Upsell Banner - Tactical Theme */}
        <div className="mt-16 relative overflow-hidden rounded-2xl border border-[#ff0000]/20">
          <div className="absolute inset-0 bg-gradient-to-r from-[#ff0000]/10 via-[#141414] to-[#ff0000]/5" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-[#ff0000]/10 via-transparent to-transparent" />
          
          <div className="relative px-6 md:px-10 py-10 md:py-14 flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center gap-2 justify-center md:justify-start mb-4">
                <Zap className="h-5 w-5 text-[#ff0000]" />
                <span 
                  className="text-[#ff0000] font-bold text-xs tracking-[0.2em] uppercase"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  Advanced Analytics
                </span>
              </div>
              <h2 
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-tight mb-4"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                AI-Powered Stats &amp; Highlights
              </h2>
              <p className="text-zinc-400 text-sm md:text-base max-w-lg mb-6">
                Get comprehensive box scores, auto-generated video highlights, and deep performance analytics for every athlete. Record the game — we do the rest.
              </p>
              <Badge className="bg-[#ff0000]/20 text-[#ff0000] border border-[#ff0000]/30 text-sm px-4 py-1.5 font-bold uppercase tracking-wide">
                Coming Soon
              </Badge>
            </div>
            
            <div className="hidden md:grid grid-cols-2 gap-3 shrink-0">
              {[
                { label: 'Box Scores', icon: BarChart3 },
                { label: 'Highlights', icon: Video },
                { label: 'Advanced Stats', icon: TrendingUp },
                { label: 'Player Stats', icon: Award },
              ].map(({ label, icon: Icon }) => (
                <div 
                  key={label} 
                  className="bg-white/5 border border-white/10 rounded-xl px-5 py-4 text-center backdrop-blur-sm hover:border-[#ff0000]/30 transition-colors duration-200"
                >
                  <Icon className="h-6 w-6 text-[#ff0000] mx-auto mb-2" />
                  <span 
                    className="text-[10px] text-zinc-400 font-bold uppercase tracking-wide"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Stats Modal */}
      <AdvancedStatsModal
        open={showStatsModal}
        onOpenChange={setShowStatsModal}
        athlete={selectedAthleteForStats}
      />
    </div>
  );
};

export default Athletes;
